/**
 * Factory Card Component
 * Displays a single factory with status, location info
 */

import { useTranslation } from '@spcc/core'
import {
  Card,
  Badge,
  Button,
  MapPin,
  Building2,
  Settings,
  CheckCircle2,
  XCircle,
  cn,
} from '@spcc/uikit'
import type { FactoryListItem, FactoryStatus } from '../../domain/types'
import { FACTORY_STATUS_CONFIG, T_KEYS } from '../../domain/constants'

// ============================================
// Constants
// ============================================

const STATUS_KEYS: Record<FactoryStatus, string> = {
  active: T_KEYS.FACTORIES.STATUS.ACTIVE,
  inactive: T_KEYS.FACTORIES.STATUS.INACTIVE,
}

// ============================================
// Helper Functions
// ============================================

function formatDate(dateString: string, locale: string): string {
  try {
    return new Date(dateString).toLocaleDateString(locale === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return '-'
  }
}

function hasLocationInfo(factory: FactoryListItem): boolean {
  return Boolean(factory.locationName || factory.address || (factory.cities && factory.cities.length > 0) || factory.city)
}

function getFactoryCities(factory: FactoryListItem): string[] {
  if (factory.cities && factory.cities.length > 0) {
    return factory.cities
  }

  if (factory.city) {
    return [factory.city]
  }

  return []
}

// ============================================
// Types
// ============================================

export interface FactoryCardProps {
  factory: FactoryListItem
  onSelect?: (factory: FactoryListItem) => void
  onSettings?: (factory: FactoryListItem) => void
}

// ============================================
// Component
// ============================================

export function FactoryCard({
  factory,
  onSelect,
  onSettings,
}: FactoryCardProps) {
  const { t, i18n } = useTranslation()

  const statusConfig = FACTORY_STATUS_CONFIG[factory.status]
  const hasLocation = hasLocationInfo(factory)
  const cities = getFactoryCities(factory)
  const locationLabel = factory.locationName || factory.address

  return (
    <Card
      className={cn(
        'glass-card p-6',
        onSelect && 'cursor-pointer hover:ring-2 hover:ring-primary/20'
      )}
      onClick={() => onSelect?.(factory)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-foreground truncate">
            {factory.name}
          </h3>
          <p className="text-sm text-muted-foreground font-mono">{factory.code}</p>
        </div>
        <div className="flex items-center gap-2 ms-3">
          <Badge
            variant="secondary"
            className={cn('gap-1', statusConfig.bgClass, statusConfig.textClass)}
          >
            {factory.status === 'active' ? (
              <CheckCircle2 className="w-3 h-3" />
            ) : (
              <XCircle className="w-3 h-3" />
            )}
            {t(STATUS_KEYS[factory.status])}
          </Badge>
          {onSettings && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={(e) => {
                e.stopPropagation()
                onSettings(factory)
              }}
            >
              <Settings className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Location Info - Always show section for consistent height */}
      <div className="space-y-2 mb-4 min-h-[72px]">
        {hasLocation ? (
          <>
            {/* Location */}
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
              {locationLabel ? (
                <span className="text-foreground truncate">{locationLabel}</span>
              ) : (
                <span className="text-muted-foreground/60 italic">
                  {t(T_KEYS.FACTORIES.CARD.NO_ADDRESS)}
                </span>
              )}
            </div>

            {/* City */}
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-muted-foreground shrink-0" />
              {cities.length > 0 ? (
                <span className="text-foreground">{cities.join(', ')}</span>
              ) : (
                <span className="text-muted-foreground/60 italic">
                  {t(T_KEYS.FACTORIES.CARD.NO_CITY)}
                </span>
              )}
            </div>

          </>
        ) : (
          /* No location info - show empty state */
          <div className="flex flex-col items-center justify-center h-full py-2 text-center">
            <MapPin className="w-6 h-6 text-muted-foreground/40 mb-1" />
            <span className="text-sm text-muted-foreground/60 italic">
              {t(T_KEYS.FACTORIES.CARD.LOCATION_NOT_SET)}
            </span>
          </div>
        )}
      </div>

      {/* Footer - Timestamps */}
      <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t border-border/50">
        <span>
          {t(T_KEYS.FACTORIES.CARD.CREATED)}: {formatDate(factory.createdAt, i18n.language)}
        </span>
        {factory.updatedAt && factory.updatedAt !== factory.createdAt && (
          <span>
            {t(T_KEYS.FACTORIES.CARD.UPDATED)}: {formatDate(factory.updatedAt, i18n.language)}
          </span>
        )}
      </div>
    </Card>
  )
}
